import { Button, Alert, Col, Row, Tag, Table, Card, Modal } from "antd";
import classes from "./IndustryDetailPage.module.scss";
import Marquee from "react-fast-marquee";
import {
  topGrowthCompaniesColumns,
  topCompaniesColumns,
  topPerformingCompaniesColumns,
} from "./constants";
import NewsGrid from "./NewsSection";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import React, { useState } from "react";
import ArticleCard from "./ArticleCard";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "../../constants";
import { getArticles } from "../../services/dashboardService";

const IndustryDetailPage = () => {
  const sectors = useSelector((state) => state.sectors);
  const { id, industryId } = useParams();
  const IndustryDetails = sectors
    .find((sector) => sector.sector_key === id)
    ?.industries.find((industry) => industry.name === industryId);

  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [currentReport, setCurrentReport] = useState(null);

  const getArticlesQuery = useQuery({
    queryKey: [queryKeys.articles],
    queryFn: () => getArticles(),
  });

  // Tables Work
  const topCompanies = Object.keys(IndustryDetails?.top_companies.name).map(
    (symbol) => ({
      key: symbol,
      symbol,
      name: IndustryDetails.top_companies.name[symbol],
      marketWeight: IndustryDetails.top_companies["market weight"][symbol],
      rating: IndustryDetails.top_companies.rating[symbol] || "N/A",
    })
  );

  const topGrowthCompanies = Object.keys(
    IndustryDetails.top_growth_companies.name
  ).map((symbol) => ({
    key: symbol,
    symbol,
    name: IndustryDetails.top_growth_companies.name[symbol],
    ytdReturn: IndustryDetails.top_growth_companies["ytd return"][symbol],
    growthEstimate:
      IndustryDetails.top_growth_companies[" growth estimate"][symbol] || "N/A",
  }));

  const topPerformingCompanies = Object.keys(
    IndustryDetails.top_performing_companies.name
  ).map((symbol) => ({
    key: symbol,
    symbol,
    name: IndustryDetails.top_performing_companies.name[symbol],
    ytdReturn: IndustryDetails.top_performing_companies["ytd return"][symbol],
    lastPrice: IndustryDetails.top_performing_companies[" last price"][symbol],
    targetPrice:
      IndustryDetails.top_performing_companies["target price"][symbol],
  }));

  // //  report work
  const showPopup = (report) => {
    setCurrentReport(report);
    setIsPopupVisible(true);
  };

  const hidePopup = () => {
    setIsPopupVisible(false);
    setCurrentReport(null);
  };

  return (
    <div className={classes.pageContent}>
      <div className={classes.headerSection}>
        <div className={classes.header}>
          <span className={classes.pageTitle}>{IndustryDetails?.name}</span>
          <span className={classes.pageSubTitle}>
            {IndustryDetails?.overview?.description ?? "-"}
          </span>
        </div>
        <div>
          <div className={classes.cardWrapper}>
            {IndustryDetails?.market_cap && (
              <div className={classes.card}>
                <h5 className={classes.cardTitle}>Market Cap</h5>
                <span className={classes.cardContent}>
                  {IndustryDetails?.overview?.market_cap ?? "-"}
                </span>
              </div>
            )}
            {IndustryDetails?.overview?.market_weight && (
              <div className={classes.card}>
                <h5 className={classes.cardTitle}>Market Weight</h5>
                <span className={classes.cardContent}>
                  {IndustryDetails?.overview?.market_weight ?? "-"}
                </span>
              </div>
            )}
            {IndustryDetails?.overview?.industries_count && (
              <div className={classes.card}>
                <h5 className={classes.cardTitle}>Industries</h5>
                <span className={classes.cardContent}>
                  {IndustryDetails?.overview?.industries_count ?? "-"}
                </span>
              </div>
            )}
            {IndustryDetails?.overview?.companies_count && (
              <div className={classes.card}>
                <h5 className={classes.cardTitle}>Companies</h5>
                <span className={classes.cardContent}>
                  {IndustryDetails?.overview?.companies_count ?? "-"}
                </span>
              </div>
            )}
            {IndustryDetails?.overview?.employee_count && (
              <div className={classes.card}>
                <h5 className={classes.cardTitle}>Employees</h5>
                <span className={classes.cardContent}>
                  {IndustryDetails?.overview?.employee_count ?? "-"}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      <Alert
        banner
        message={
          <Marquee pauseOnHover gradient={false}>
            Sector performance is calculated based on the previous closing price
            of all sector constituents and is weighted by market capitalization.
          </Marquee>
        }
      />
      <span className={classes.selectSector} style={{ marginTop: "10px" }}>
        Top Companies in This Sector
      </span>
      <Row gutter={[16, 16]}>
        <Col md={24}>
          <Table
            columns={topCompaniesColumns}
            dataSource={topCompanies}
            size="small"
            pagination={false}
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: "5px",
              maxHeight: "500px",
              overflowY: "auto",
            }}
            sticky
          />
        </Col>
        <Col span={12}>
          <span className={classes.selectSector}>Top Growth Companies</span>
          <Table
            columns={topGrowthCompaniesColumns}
            dataSource={topGrowthCompanies}
            size="small"
            pagination={false}
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: "5px",
              maxHeight: "500px",
              overflowY: "auto",
              minHeight: "300px",
            }}
            sticky
          />
        </Col>
        <Col span={12}>
          <span className={classes.selectSector}>Top Performing Companies</span>
          <Table
            columns={topPerformingCompaniesColumns}
            dataSource={topPerformingCompanies}
            size="small"
            pagination={false}
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: "5px",
              maxHeight: "500px",
              overflowY: "auto",
              minHeight: "300px",
            }}
            sticky
          />
        </Col>
      </Row>
      <span className={classes.selectSector}>Latest News</span>
      <NewsGrid IndustryDetails={IndustryDetails} />
      <div className={classes.reportsSection}>
        <span className={classes.selectSector}>Research Reports</span>
        <br />
        <Row gutter={[16, 16]}>
          {IndustryDetails?.research_reports.map((report) => {
            const formattedDate = new Date(
              report.reportDate
            ).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "long",
              year: "numeric",
            });
            const titleWithoutDate = report.headHtml
              .replace(
                /\d{1,2}\/\d{1,2}\/\d{4}|\d{1,2}-\d{1,2}-\d{4}|\d{1,2}\s+\w+\s+\d{4}/g,
                ""
              )
              .trim();

            return (
              <Col key={report.id} span={12}>
                <Card hoverable className={classes.reportCard}>
                  <h4 className={classes.reportTitle}>
                    {titleWithoutDate} - {formattedDate}
                  </h4>
                  <p className={classes.reportDescription}>
                    {report.reportTitle.length > 100
                      ? `${report.reportTitle.substring(0, 100)}...`
                      : report.reportTitle}
                  </p>
                  <div className={classes.cardFooter}>
                    <div className={classes.tags}>
                      <Tag color="blue">{report.provider}</Tag>
                      <Tag color="green">{report.reportType}</Tag>
                    </div>
                    {report.reportTitle.length > 100 && (
                      <Button
                        type="link"
                        className={classes.readMoreButton}
                        onClick={() => showPopup(report)}
                      >
                        Read More
                      </Button>
                    )}
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      </div>
      <br />
      <span className={classes.selectSector}>Articles</span>
      <Row gutter={[16, 16]}>
        {(getArticlesQuery.data?.data?.content
          ? getArticlesQuery.data?.data?.content
          : []
        ).map((article, index) => (
          <Col key={index} xs={24} sm={12} lg={8}>
            <ArticleCard article={article} />
          </Col>
        ))}
      </Row>

      {isPopupVisible && (
        <Modal
          title="Full Report"
          visible={isPopupVisible}
          onCancel={hidePopup}
          footer={null}
        >
          <p>{currentReport?.reportTitle}</p>
        </Modal>
      )}
    </div>
  );
};

export default IndustryDetailPage;
